/**
 * Design System — Web ThemeProvider.
 *
 * Paints the active `Theme` (default: SLDS) onto `:root` as `--color-*` / token
 * CSS variables, tracks light/dark/system via the shared `useTheme` hook, and
 * overlays a per-tenant white-label override (MF-14). Because it emits the same
 * `--color-*` names the shell + colors.css use, mounting it themes the entire
 * app from one place.
 *
 * Multi-tenant: `tenantOverride` is resolved by the host (from the branding API
 * per the logged-in tenant) and passed in; the admin edits it in the Branding /
 * Theme screen. Omitting it renders the default SLDS theme.
 *
 * `useThemeContext()` returns a safe default outside a provider so unit/screen
 * tests that don't wrap ThemeProvider keep working.
 */
import { createContext, createElement, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTheme, type ThemeMode as HookThemeMode } from '../hooks/useTheme.js';
import { defaultTheme, type Theme, type TenantThemeOverride } from './theme.js';
import { buildThemeVars } from './cssVars.js';

export interface ThemeContextValue {
  readonly theme: Theme;
  readonly mode: HookThemeMode;
  readonly effective: 'light' | 'dark';
  readonly setMode: (m: HookThemeMode) => void;
  readonly tenantOverride?: TenantThemeOverride;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export interface ThemeProviderProps {
  readonly children: ReactNode;
  /** Base design theme. Defaults to SLDS. */
  readonly theme?: Theme;
  /** Per-tenant white-label override (from branding API). MF-14. */
  readonly tenantOverride?: TenantThemeOverride;
}

function paintRoot(theme: Theme, effective: 'light' | 'dark', override?: TenantThemeOverride): void {
  if (typeof document === 'undefined') return;
  const rootStyle = document.documentElement.style;
  const vars = buildThemeVars(theme, effective, override);
  for (const key in vars) rootStyle.setProperty(key, vars[key]);
}

/**
 * Imperatively paint a theme (+ optional per-tenant override) onto `:root`.
 * Exposed so the host can re-apply a tenant's branding after auth without
 * re-mounting the provider (see TenantThemeSync).
 */
export function applyThemeToRoot(
  theme: Theme,
  effective: 'light' | 'dark',
  override?: TenantThemeOverride,
): void {
  paintRoot(theme, effective, override);
}

export function ThemeProvider({ children, theme = defaultTheme, tenantOverride }: ThemeProviderProps): ReactNode {
  const { mode, effective, setMode } = useTheme();

  useEffect(() => {
    paintRoot(theme, effective, tenantOverride);
  }, [theme, effective, tenantOverride]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, mode, effective, setMode, tenantOverride }),
    [theme, mode, effective, setMode, tenantOverride],
  );

  return createElement(ThemeContext.Provider, { value }, children);
}

/** Access the active theme + mode. Safe default (SLDS/light) outside a provider. */
export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  return { theme: defaultTheme, mode: 'system', effective: 'light', setMode: () => undefined };
}
